import { lazy, Suspense, useState } from 'react'

import { AppBar, Box, CircularProgress, Tab, Tabs } from '@mui/material'
import { useHistory } from 'react-router-dom'

import { TabPanel } from 'components/common'
import { CloneProps, useHashToSelectTab } from 'helpers'
import { PermissionType, Restricted } from 'providers'

const AccountTab = lazy(() => import('./AccountTab'))
const NotificationsTab = lazy(() => import('./NotificationsTab'))
const DriverOnboardingDialog = lazy(() =>
  import('components/suppliers/drivers').then((m) => ({ default: m.DriverOnboardingDialog })),
)

enum TabId {
  Account = 0,
  Notifications,
}

const tabsList = [
  {
    id: TabId.Account,
    label: 'Account',
    hash: '#account',
    policy: 'ViewProfile' as PermissionType,
  },
  {
    id: TabId.Notifications,
    label: 'Notifications',
    hash: '#notifications',
    policy: 'ViewProfileNotificationSettings' as PermissionType,
  },
]

function commonProps(index: TabId) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  }
}

const Profile = () => {
  const [tabValue, setTabValue] = useState<number | false>(false)
  const history = useHistory()

  useHashToSelectTab(tabsList, setTabValue)

  const handleTabChange = (_, newValue: TabId) => {
    const selectedTab = tabsList.find((tab) => tab.id === newValue)
    if (selectedTab) {
      history.push(selectedTab.hash)
    }
  }

  const suspenseFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
      <CircularProgress />
    </Box>
  )

  return (
    <>
      <AppBar position='static' color='default'>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label='profile tabs'
          variant='scrollable'>
          {tabsList.map((tab) => (
            <CloneProps key={tab.id}>
              {(tabProps) => (
                <Restricted to={tab.policy}>
                  <Tab {...tabProps} {...commonProps(tab.id)} label={tab.label} value={tab.id} />
                </Restricted>
              )}
            </CloneProps>
          ))}
        </Tabs>
      </AppBar>

      <Restricted to='ViewProfile'>
        <TabPanel value={tabValue} index={TabId.Account}>
          <Suspense fallback={suspenseFallback}>
            <AccountTab />
          </Suspense>
        </TabPanel>
      </Restricted>

      <Restricted to='ViewProfileNotificationSettings'>
        <TabPanel value={tabValue} index={TabId.Notifications}>
          <Suspense fallback={suspenseFallback}>
            <NotificationsTab />
          </Suspense>
        </TabPanel>
      </Restricted>

      <Restricted to='ViewOnboardingProcess'>
        <Suspense fallback={suspenseFallback}>
          <DriverOnboardingDialog />
        </Suspense>
      </Restricted>
    </>
  )
}

export default Profile
